// 账号/同步 Context:挂载时把本地进度上报合并(升级匿名号/跨设备同步),
// 之后暴露 me(资料/连胜/等级)给全站。后端不可用时静默降级(纯本地)。
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { fetchMe, syncProgress, logout as apiLogout, type Me } from './lib/api';
import { todayKey } from './lib/game';
import { getModeState, getStreak } from './lib/storage';
import { MODES } from './lib/daily';

interface AccountCtx {
  me: Me | null;
  loading: boolean;
  refresh: () => void;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AccountCtx>({ me: null, loading: true, refresh: () => {}, signOut: async () => {} });

function endlessBests(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const m of [...MODES, 'connections']) {
    try {
      const v = localStorage.getItem(`geodle:endless:${m}`);
      const best = v ? JSON.parse(v)?.best ?? 0 : 0;
      if (best > 0) out[m] = best;
    } catch {
      /* ignore */
    }
  }
  return out;
}

function localProgress() {
  const day = todayKey();
  const results = MODES.map((m) => {
    const s = getModeState(m, day);
    // Draw 的 ModeState.guesses 存的是 [分数];results.guesses 列语义是「越小越好」,
    // 故上报罚分 100-score(与 Connections 复用 guesses 列存 mistakes 同理)。
    const guesses =
      m === 'draw'
        ? 100 - Math.max(0, Math.min(100, Number(s.guesses[0] ?? 0)))
        : s.guesses.length;
    return { mode: m, solved: s.status === 'won', guesses };
  }).filter((r) => r.solved || r.guesses > 0);
  return { day, localStreak: getStreak(), endlessBests: endlessBests(), results };
}

export function AccountProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      // 同步会上报本地进度并返回服务端真相(首次=迁移/升级匿名号)。
      const m = await syncProgress(localProgress());
      setMe(m);
    } catch {
      // 同步失败时退而拉取;再失败则后端不可用,保持纯本地。
      try {
        setMe(await fetchMe());
      } catch {
        setMe(null);
      }
    } finally {
      setLoading(false);
      // 同步完成(服务端已是最新)→ 通知排行榜/统计静默重拉。
      window.dispatchEvent(new Event('geodle:synced'));
    }
  }

  useEffect(() => {
    refresh();
    // 每日通关 / 无尽连胜更新后,顺手再同步一次(上报含无尽最大连胜)。
    const h = () => refresh();
    window.addEventListener('geodle:progress', h);
    window.addEventListener('geodle:endless', h);
    return () => {
      window.removeEventListener('geodle:progress', h);
      window.removeEventListener('geodle:endless', h);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function signOut() {
    await apiLogout();
    await refresh();
  }

  return <Ctx.Provider value={{ me, loading, refresh, signOut }}>{children}</Ctx.Provider>;
}

export function useAccount(): AccountCtx {
  return useContext(Ctx);
}
